import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, MessageSquare, Mail, Share2 } from "lucide-react";
import { toast } from "sonner";
import QRCode from "@/components/shared/QRCode";

interface ShareDialogProps {
  url: string;
  title: string;
  subtitle?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ShareDialog({
  url,
  title,
  subtitle,
  open,
  onOpenChange,
}: ShareDialogProps) {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Länk kopierad!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kunde inte kopiera länken");
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title,
        text: subtitle ?? title,
        url,
      });
    } catch {
      // User cancelled or not supported — fall back silently
    }
  };

  const smsUrl = `sms:?body=${encodeURIComponent(`${title}: ${url}`)}`;
  const whatsAppUrl = `https://wa.me/?text=${encodeURIComponent(`${title}: ${url}`)}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${subtitle ? subtitle + "\n\n" : ""}${url}`)}`;

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Dela</DialogTitle>
          {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-4">
          {/* URL + Copy */}
          <div className="flex gap-2">
            <Input value={url} readOnly className="font-mono text-xs" />
            <Button variant="outline" size="icon" onClick={copyLink}>
              {copied ? (
                <Check className="h-4 w-4 text-primary" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Share channels */}
          <div className="grid grid-cols-2 gap-2">
            {canNativeShare && (
              <Button
                variant="outline"
                className="gap-2"
                onClick={handleNativeShare}
              >
                <Share2 className="h-4 w-4" />
                Dela
              </Button>
            )}

            <Button variant="outline" className="gap-2" asChild>
              <a href={smsUrl}>
                <MessageSquare className="h-4 w-4" />
                SMS
              </a>
            </Button>

            <Button variant="outline" className="gap-2" asChild>
              <a
                href={whatsAppUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </a>
            </Button>

            <Button variant="outline" className="gap-2" asChild>
              <a href={mailtoUrl}>
                <Mail className="h-4 w-4" />
                E-post
              </a>
            </Button>
          </div>

          {/* QR Code */}
          <div className="flex flex-col items-center gap-2 pt-2">
            <QRCode value={url} size={160} />
            <p className="text-xs text-muted-foreground">
              Skanna QR-koden för att öppna länken
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
